<template>
  <div class="tw:md:flex tw:md:items-center" v-if="!hasNoAccommodations">
    <button
      class="tw:flex tw:justify-between tw:items-center tw:text-sm tw:py-1.5 tw:my-1 tw:w-full tw:px-4 tw:md:hidden"
      @click="toggleMobileAppNav"
      @keydown="toggleMobileAppNav"
    >
      <div class="tw:flex tw:items-center">
        <span class="tw:text-base tw:font-bold tw:text-blue-700">{{ $t(activeLink?.name) }}</span>
        <span
          v-if="activeLink.badge"
          class="tw:px-2 tw:leading-[17px] tw:text-[10px] tw:ml-2 tw:text-white tw:font-bold tw:bg-red-700 tw:rounded-tl-2xl tw:rounded-br-2xl"
          >{{ $t(activeLink.badge) }}</span
        >
      </div>
      <div
        class="tw:h-8 tw:rounded-full tw:w-8 tw:relative tw:bg-gray-200 tw:hover:bg-gray-300 tw:right-0 tw:transition-all tw:ease-in-out tw:duration-200"
      >
        <div
          :class="{
            'down-triangle': !isMobileAppNavOpen,
            'up-triangle': isMobileAppNavOpen,
          }"
          class="tw:z-0 tw:h-2 tw:w-2 tw:absolute tw:right-0"
        ></div>
      </div>
    </button>
    <hr v-if="isMobileAppNavOpen" class="tw:md:hidden" />
    <div v-if="isAuthLoading" class="tw:h-[56px] tw:flex tw:items-center tw:rounded-lg">
      <UiNavigationLink
        :ref="$t('navigation.home')"
        to="/"
        tag="router-link"
        isUnderlineHover
        class="tw:text-gray-800 tw:hover:text-blue-700 tw:relative tw:text-base tw:md:text-sm ui-navigation-link tw:px-4 tw:md:px-0 tw:h-full"
        @onClick="closeMobileNav()"
      >
        {{ $t('navigation.home') }}
      </UiNavigationLink>
    </div>
    <ul
      v-else-if="navLinks.length > 0"
      class="tw:w-full tw:justify-between tw:flex tw:md:items-center tw:flex-col tw:md:flex-row"
    >
      <ul
        :class="{ 'tw:hidden': !isMobileAppNavOpen }"
        class="tw:md:flex tw:items-center tw:justify-center tw:md:h-14 tw:relative tw:py-1 tw:md:py-0"
      >
        <li
          v-for="(app, index) in navLinks"
          :key="index"
          data-cy="app-list"
          class="ui-navigation-list-item tw:md:ml-8 tw:xl:ml-10 tw:h-full"
        >
          <UiNavigationLink
            :dataTestId="`app-${app.name}`"
            :ref="$t(app?.name)"
            :to="app.to"
            :is-icon="app.icon !== null"
            isUnderlineHover
            tag="router-link"
            class="tw:text-gray-800 tw:hover:text-blue-700 tw:relative tw:text-base tw:md:text-sm ui-navigation-link tw:px-4 tw:md:px-0 tw:h-full"
            @onClick="closeMobileNav()"
          >
            {{ $t(app?.name) }}
            <span
              v-if="app.badge"
              class="tw:md:absolute tw:left-0 tw:md:ml-0 tw:ml-2 tw:top-0 tw:px-2 tw:md:py-0 tw:md:py-auto tw:text-[10px] tw:leading-[17px] tw:text-white tw:font-bold tw:bg-red-700 tw:rounded-tl-2xl tw:rounded-br-2xl"
              >{{ $t(app.badge) }}</span
            >
            <span
              v-if="app.isDot"
              class="tw:bg-red-700 tw:h-[8px] tw:w-[8px] tw:rounded-full tw:absolute tw:right-[-10px] tw:top-[15px]"
            />
          </UiNavigationLink>
        </li>
      </ul>
      <hr v-if="isMobileAppNavOpen" class="tw:md:hidden" />
      <ul
        :class="{ 'tw:hidden': !isMobileAppNavOpen }"
        class="tw:md:flex tw:items-center tw:justify-center tw:md:h-14 tw:relative tw:my-1 tw:md:m-0 tw:px-4 tw:md:px-0"
      >
        <CheckPropertyLink source="navbar" v-if="selectedAccommodationId" />
      </ul>
    </ul>
  </div>
</template>

<script setup>
import { useRoute, useRouter } from 'vue-router'
import { computed, ref, onMounted, watch, onBeforeUnmount } from 'vue'

import UiNavigationLink from '@/components/dashboard/ui/UiNavigationLink.vue'
import CheckPropertyLink from '@/components/dashboard/CheckPropertyLink.vue'
import { selectedAccommodationId, useOrganisationAssignments } from '@/layouts/queries'
import { useSessionValues } from '@/composables/useSessionValues'
import { useStudioPlusAuth } from '@/composables/useStudioPlusAuth'
import { useRateConnectAuth } from '@/composables/useRateConnectAuth'

const route = useRoute()
const router = useRouter()
const props = defineProps({
  isMobileAppNavOpen: Boolean,
  isMobile: Boolean,
})

const emit = defineEmits(['toggleMobileAppNav'])
const toggleMobileAppNav = () => emit('toggleMobileAppNav')
const { selectedOrganisationId } = useSessionValues()
const { hasStudioPlus, isAuthLoading } = useStudioPlusAuth()

const currentRouteName = computed(() => route.name)
const { assignmentsData } = useOrganisationAssignments(selectedOrganisationId)
const hasNoAccommodations = computed(
  () => !assignmentsData.value || !Array.isArray(assignmentsData.value) || assignmentsData.value?.length === 0
)
const updateChecked = ref(false)
const isUpdateChecked = () => {
  let updateState

  try {
    updateState = window.localStorage.getItem(`property_details_news_${selectedAccommodationId.value}`)
  } catch {
    return false
  }

  updateChecked.value = updateState === 'true'
}
const { getDefaultRoute, isLoading: isRCAuthLoading } = useRateConnectAuth()

const rcLink = computed(() => {
  if (isRCAuthLoading.value) {
    return {
      to: '/rate-connect',
      name: 'app.rateConnect.name',
      isActive: currentRouteName.value.includes('rate-connect'),
    }
  }

  const defaultRoute = getDefaultRoute()
  const path = defaultRoute ? router.resolve(defaultRoute).path : '/rate-connect'

  return {
    to: path, // Now it's a string!
    name: 'app.rateConnect.name',
    isActive: currentRouteName.value.includes('rate-connect'),
  }
})

const navLinks = computed(() => {
  const homeLink = {
    to: '/',
    name: 'navigation.home',
    isActive: currentRouteName.value.includes('home'),
  }
  const rcLinkValue = rcLink.value
  const studioPlusInsightsLink = {
    to: '/studio-plus-insights',
    name: 'navigation_menu_business_studio_plus',
    badge: 'business_studio_plus_new_flag',
    isActive: currentRouteName.value.includes('studio-plus-insights'),
  }
  const propertyDetailsLink = {
    to: '/property-details',
    isDot: !updateChecked.value,
    name: 'app.propertyDetails.name',
    isActive: currentRouteName.value.includes('property-details'),
  }
  const rateInsightsLink = {
    to: '/rate-insights',
    name: 'app.rateInsights.name',
    isActive: currentRouteName.value.includes('rate-insights'),
  }
  const visitorsProfileLink = {
    to: '/visitors-profile',
    name: 'app.visitorsProfile.name',
    isActive: currentRouteName.value.includes('visitors-profile'),
  }
  const hotelOffersLink = {
    to: '/hotel-offers',
    name: 'hotel_offers_product_header',
    isActive: currentRouteName.value.includes('hotel-offers'),
  }
  const studioPlusLink = {
    to: '/studio-plus/compare',
    badge: 'business_studio_plus_new_flag',
    name: 'navigation_menu_business_studio_plus',
    isActive: currentRouteName.value.includes('studio-plus'),
  }

  // Base links always shown
  const baseLinks = [homeLink, rcLinkValue, propertyDetailsLink]

  if (hasNoAccommodations.value || !selectedAccommodationId.value) return baseLinks

  // Use hasStudioPlus from the composable — single source of truth
  if (hasStudioPlus.value && !props.isMobile) {
    // Has active Plus subscription (desktop) → show all Plus features
    return [
      homeLink,
      rcLinkValue,
      studioPlusInsightsLink,
      hotelOffersLink,
      rateInsightsLink,
      visitorsProfileLink,
      propertyDetailsLink,
    ]
  }

  if (hasStudioPlus.value && props.isMobile) {
    // Has active Plus subscription (mobile) → show limited features
    return [homeLink, rcLinkValue, studioPlusInsightsLink, propertyDetailsLink]
  }

  // No Plus → show subscribe link
  return [homeLink, rcLinkValue, studioPlusLink, propertyDetailsLink]
})

const activeLink = computed(() => {
  const active = navLinks.value.find(item => item.isActive)
  if (active) return active

  const byId = navLinks.value.find(item => item.app_id === route.params.id)
  if (byId) return byId

  return navLinks.value[0]
})

const closeMobileNav = () => {
  toggleMobileAppNav()
}

onMounted(() => isUpdateChecked())
const updateCheckTimer = ref(null)
watch(currentRouteName, () => {
  if (!updateChecked.value) {
    if (updateCheckTimer.value) {
      clearTimeout(updateCheckTimer.value)
    }
    updateCheckTimer.value = setTimeout(isUpdateChecked, 1000)
  }
})

watch(selectedAccommodationId, () => {
  if (updateCheckTimer.value) {
    clearTimeout(updateCheckTimer.value)
  }
  updateCheckTimer.value = setTimeout(isUpdateChecked, 1000)
})

onBeforeUnmount(() => {
  if (updateCheckTimer.value) {
    clearTimeout(updateCheckTimer.value)
  }
})
</script>

<style scoped>
a.router-link-active {
  font-weight: bold;
  color: var(--tw-color-blue-700);
}
.ui-navigation-list-item:first-child {
  margin-left: 0;
}

@media (min-width: 768px) {
  .ui-navigation-list-item:first-child {
    display: inline;
  }
}

.ui-navigation-list-item:last-child {
  margin-right: 1rem;
}

.ui-navigation-link .app-icon {
  filter: grayscale(1);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
.ui-navigation-link:hover .app-icon,
.router-link-active .app-icon {
  filter: grayscale(0) !important;
}
.down-triangle {
  border-left-width: 4px;
  border-right-width: 4px;
  border-top-width: 4px;
  top: 15px;
  left: 12px;
  border-left-color: var(--tw-color-transparent);
  border-right-color: var(--tw-color-transparent);
  border-top-color: var(--tw-color-black);
}

.up-triangle {
  border-left-width: 4px;
  border-right-width: 4px;
  border-top-width: 4px;
  top: 10px;
  right: 12px;
  border-left-color: var(--tw-color-transparent);
  border-right-color: var(--tw-color-transparent);
  border-top-color: var(--tw-color-black);
  transform: rotate(180deg);
}
</style>
